<template>
  <v-card>
    <v-card-title>
      <slot name="title" />
    </v-card-title>
    <v-divider />
    <v-card-text class="pt-4">
      <v-form v-model="valid">
        <v-text-field
          disabled
          outlined
          label="Owner"
          :value="owner && owner.name"
          required
        />
        <v-select
          v-model="selectedCategoryId"
          outlined
          :items="categories"
          item-text="name"
          item-value="id"
          label="Category"
          :rules="[v => !!v || 'Category is required']"
        />
        <v-text-field
          v-model="form.name"
          outlined
          label="Schedule name"
          placeholder="Please enter schedule name"
          :rules="[
            v => v !== '' || 'Name is required',
            v => (v && v.length <= 191) || 'Name must be less than 191 characters'
          ]"
          required
        />
        <v-row no-gutters>
          <v-col cols="9">
            <v-text-field
              v-model="form.cron"
              outlined
              label="Cron"
              placeholder="Please enter cron time"
              :hint="cronText"
              persistent-hint
              :rules="[
                v => v !== '' || 'Cron is required',
                v => v.split(' ').length < 6 || 'Can only have 5 parts',
                v => !cronText.includes('Error') || cronText,
                v => !cronText.includes('undefined') || 'Incurrect cron'
              ]"
              required
            />
          </v-col>
          <v-col
            class="pl-3"
            cols="3"
          >
            <v-checkbox
              v-model="form.useJob"
              :label="`useJob`"
            />
          </v-col>
        </v-row>
        <v-menu
          ref="menu"
          v-model="dateMenu"
          outlined
          :close-on-content-click="false"
          :return-value.sync="form.excludeDays"
          transition="scale-transition"
          offset-x
          min-width="290px"
        >
          <template #activator="{ on, attrs }">
            <v-combobox
              v-model="form.excludeDays"
              multiple
              chips
              small-chips
              outlined
              clearable
              label="Exclude Days"
              append-icon="fas fa-calendar-alt"
              placeholder="Please select exclude days"
              readonly
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="form.excludeDays"
            multiple
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="dateMenu = false"
            >
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="$refs.menu.save(form.excludeDays)"
            >
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
        <v-autocomplete
          v-model="form.specId"
          outlined
          :items="selectedSpecs"
          item-text="name"
          item-value="id"
          label="Spec"
          placeholder="Type to Search Spec name"
          :rules="[v => !!v || 'Spec is required']"
        />

        <v-text-field
          v-model="form.spec.method"
          disabled
          label="Method"
          auto-grow
          outlined
          required
        />
        <v-textarea
          v-model="form.spec.endpoint"
          disabled
          label="Endpoint"
          auto-grow
          rows="2"
          outlined
          required
        />

        <v-divider />
        <scheduler-request-field
          v-if="Object.keys(form.spec.headersSchema).length > 0"
          :key="'head' + form.specId"
          v-model="form.headers"
          name="Header"
          :schema="form.spec.headersSchema"
        />
        <scheduler-request-field
          v-if="Object.keys(form.spec.schema).length > 0"
          key="form.specId"
          v-model="form.payload"
          name="Payload"
          :schema="form.spec.schema"
        />
      </v-form>
    </v-card-text>
    <v-divider />
    <slot name="message" />
    <v-card-actions>
      <v-spacer />
      <slot name="actions" />
    </v-card-actions>
  </v-card>
</template>

<script>
import { user } from '@rhitics/core'
import { cloneDeep } from 'lodash'
import { toString as cronToString } from 'cronstrue'
import SchedulerRequestField from '../SchedulerRequestField'

export default {
  name: 'TaskForm',
  components: {
    SchedulerRequestField
  },
  props: {
    specs: {
      type: Array
    },
    categories: {
      type: Array
    },
    task: {
      type: Object
    }
  },
  data() {
    return {
      dateMenu: false,
      specLoading: false,
      selectedSpecs: [],
      selectedCategoryId: 0,
      valid: false,
      form: {
        ownerId: NaN,
        name: '',
        specId: -1,
        spec: {
          id: NaN,
          name: '',
          categoryId: NaN,
          description: '',
          method: 'GET',
          endpoint: '',
          schema: {},
          headersSchema: {}
        },
        payload: {},
        headers: {},
        timezone: '',
        cron: '',
        excludeDays: [],
        active: false,
        useJob:false
      },
      owner: null
    }
  },
  computed: {
    cronText() {
      try {
        if (this.form.cron) {
          const tmp = cronToString(this.form.cron, { use24HourTimeFormat: true })
          return tmp
        }
      } catch (error) {
        return error
      }
      return 'undefined'
    }
  },
  watch: {
    valid() {
      this.$emit('valid', this.valid)
    },
    selectedCategoryId() {
      this.selectedSpecs = this.specs.filter(spec => spec.categoryId === this.selectedCategoryId)
      if (this.form.spec?.categoryId !== this.selectedCategoryId) {
        this.form.specId = 0
        this.form.payload = {}
        this.form.headers = {}
      }
    },
    'form.specId'() {
      if (this.form.specId === 0) {
        this.form.spec.id = 0
        this.form.spec.name = ''
        this.form.spec.categoryId = NaN
        this.form.spec.description = ''
        this.form.spec.method = 'GET'
        this.form.spec.endpoint = ''
        this.form.spec.schema = {}
        this.form.spec.headersSchema = {}
      } else {
        if (this.selectedSpecs.length > 0)
          this.form.spec = cloneDeep(this.selectedSpecs.find(v => v.id === this.form.specId))
      }
    },
    form: {
      deep: true,
      handler() {
        this.$emit('update', this.form)
      }
    }
  },
  async created() {
    await this.init()
  },

  methods: {
    async init() {
      if (this.task) {
        this.form = cloneDeep(this.task)
        this.owner = (await user.fetch([this.form.ownerId]))?.data[0]
        this.selectedCategoryId = this.form.spec.categoryId
        this.form.spec = cloneDeep(this.specs.find(s => s.id === this.form.specId))
      }
    }
  }
}
</script>

<style></style>
